import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const K_LABEL = { CRYPTO: "ticker.crypto", STOCKS: "ticker.stocks", FX: "ticker.fx", COMMODITIES: "ticker.commodities" };

/**
 * AICore — abstract "AI brain" visual for the hero.
 * Concentric pulsing rings + rotating hex frame + orbiting market nodes
 * (CRYPTO / STOCKS / FX / COMMODITIES) connected by signal lines.
 * Pure SVG + framer-motion. Sized via prop.
 */
const MARKET_NODES = [
    { tag: "BTC", k: "CRYPTO", angle: 18 },
    { tag: "ETH", k: "CRYPTO", angle: 60 },
    { tag: "SPX", k: "STOCKS", angle: 110 },
    { tag: "NDX", k: "STOCKS", angle: 152 },
    { tag: "EUR", k: "FX", angle: 198 },
    { tag: "JPY", k: "FX", angle: 240 },
    { tag: "XAU", k: "COMMODITIES", angle: 286 },
    { tag: "OIL", k: "COMMODITIES", angle: 330 },
];

const polar = (cx, cy, r, deg) => {
    const a = (deg * Math.PI) / 180;
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
};

export const AICore = ({ size = 520, className = "" }) => {
    const { t } = useTranslation();
    const cx = size / 2;
    const cy = size / 2;
    const orbitR = size * 0.42;
    const innerR = size * 0.18;

    const nodes = useMemo(
        () =>
            MARKET_NODES.map((n) => {
                const [x, y] = polar(cx, cy, orbitR, n.angle);
                return { ...n, x, y };
            }),
        [cx, cy, orbitR]
    );

    return (
        <div className={`relative ${className}`} style={{ width: size, height: size }}>
            <svg
                viewBox={`0 0 ${size} ${size}`}
                className="absolute inset-0 w-full h-full"
                aria-hidden="true"
            >
                <defs>
                    <radialGradient id="core-glow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#8EE7FF" stopOpacity="0.95" />
                        <stop offset="40%" stopColor="#6E3CFF" stopOpacity="0.55" />
                        <stop offset="100%" stopColor="#000000" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient id="core-line" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#6E3CFF" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.5" />
                    </linearGradient>
                    <filter id="core-blur"><feGaussianBlur stdDeviation="0.4" /></filter>
                </defs>

                {/* outer subtle ring */}
                <motion.circle
                    cx={cx}
                    cy={cy}
                    r={orbitR + 18}
                    fill="none"
                    stroke="rgba(255,255,255,0.05)"
                    strokeWidth="1"
                    animate={{ opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* rotating hex frame */}
                <motion.g
                    style={{ originX: `${cx}px`, originY: `${cy}px` }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
                >
                    <polygon
                        points={Array.from({ length: 6 })
                            .map((_, i) => {
                                const [x, y] = polar(cx, cy, orbitR + 4, i * 60 - 90);
                                return `${x},${y}`;
                            })
                            .join(" ")}
                        fill="none"
                        stroke="rgba(255,255,255,0.08)"
                        strokeWidth="1"
                    />
                </motion.g>

                {/* pulsing rings */}
                {[0, 1, 2].map((i) => (
                    <motion.circle
                        key={`ring-${i}`}
                        cx={cx}
                        cy={cy}
                        r={innerR + 30 + i * 18}
                        fill="none"
                        stroke="rgba(110,60,255,0.35)"
                        strokeWidth="1"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                            opacity: [0, 0.5, 0],
                            scale: [0.8, 1.5, 1.8],
                        }}
                        transition={{
                            duration: 5,
                            repeat: Infinity,
                            ease: "easeOut",
                            delay: i * 1.2,
                        }}
                        style={{ originX: `${cx}px`, originY: `${cy}px` }}
                    />
                ))}

                {/* signal lines from core to nodes */}
                <g filter="url(#core-blur)">
                    {nodes.map((n, i) => (
                        <motion.line
                            key={`line-${n.tag}`}
                            x1={cx}
                            y1={cy}
                            x2={n.x}
                            y2={n.y}
                            stroke="url(#core-line)"
                            strokeWidth="0.7"
                            strokeDasharray="3 4"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: [0.15, 0.55, 0.15] }}
                            transition={{
                                pathLength: { duration: 1.4, delay: 0.3 + i * 0.08 },
                                opacity: {
                                    duration: 6,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: i * 0.4,
                                },
                            }}
                        />
                    ))}
                </g>

                {/* core glow */}
                <circle cx={cx} cy={cy} r={innerR * 1.6} fill="url(#core-glow)" opacity="0.55" />

                {/* core hexagon */}
                <motion.g
                    style={{ originX: `${cx}px`, originY: `${cy}px` }}
                    animate={{ rotate: -360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                >
                    <polygon
                        points={Array.from({ length: 6 })
                            .map((_, i) => {
                                const [x, y] = polar(cx, cy, innerR, i * 60);
                                return `${x},${y}`;
                            })
                            .join(" ")}
                        fill="rgba(0,0,0,0.6)"
                        stroke="url(#core-line)"
                        strokeWidth="1.2"
                    />
                    <polygon
                        points={Array.from({ length: 6 })
                            .map((_, i) => {
                                const [x, y] = polar(cx, cy, innerR * 0.55, i * 60 + 30);
                                return `${x},${y}`;
                            })
                            .join(" ")}
                        fill="none"
                        stroke="rgba(0,229,255,0.6)"
                        strokeWidth="0.8"
                    />
                </motion.g>

                {/* center pulse dot */}
                <motion.circle
                    cx={cx}
                    cy={cy}
                    r="3"
                    fill="#00E5FF"
                    animate={{ opacity: [0.5, 1, 0.5], scale: [0.9, 1.3, 0.9] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    style={{ originX: `${cx}px`, originY: `${cy}px` }}
                />

                {/* orbiting nodes */}
                {nodes.map((n, i) => (
                    <g key={`n-${n.tag}`}>
                        <motion.circle
                            cx={n.x}
                            cy={n.y}
                            r={5}
                            fill="#0a0a0c"
                            stroke="#00E5FF"
                            strokeWidth="1"
                            animate={{
                                opacity: [0.6, 1, 0.6],
                                scale: [0.9, 1.15, 0.9],
                            }}
                            transition={{
                                duration: 3 + (i % 3),
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: i * 0.2,
                            }}
                            style={{ originX: `${n.x}px`, originY: `${n.y}px` }}
                        />
                    </g>
                ))}
            </svg>

            {/* Node labels (DOM, easier typography control) */}
            {nodes.map((n, i) => {
                const [lx, ly] = polar(cx, cy, orbitR + 30, n.angle);
                // Push outward from center
                const align =
                    Math.cos((n.angle * Math.PI) / 180) > 0 ? "left" : "right";
                return (
                    <motion.div
                        key={`l-${n.tag}`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 0.9, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.6 + i * 0.07 }}
                        className="absolute font-mono uppercase pointer-events-none"
                        style={{
                            left: align === "left" ? lx : "auto",
                            right: align === "right" ? size - lx : "auto",
                            top: ly - 10,
                            textAlign: align,
                            transform: `translateX(${align === "left" ? "0" : "0"})`,
                        }}
                    >
                        <div className="text-[10px] tracking-[0.22em] text-white/85 leading-none">
                            {n.tag}
                        </div>
                        <div className="text-[8.5px] tracking-[0.22em] text-white/35 leading-none mt-1">
                            {K_LABEL[n.k] ? t(K_LABEL[n.k]) : n.k}
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};

export default AICore;
